import React, { useEffect, useState } from 'react';
import {
  fetchAccessRequests,
  approveAccessRequest,
  rejectAccessRequest,
  login,
  updateTokenAuditRateLimit,
  renewAccessRequest,
  getTokenLlmConfig,
  upsertTokenLlmConfig,
  deleteTokenLlmConfig,
  getOrgLlmConfig,
  upsertOrgLlmConfig,
  deleteOrgLlmConfig
} from './api';
import type {
  AccessRequestItem,
  AccessRequestStatus,
  ApproveAccessRequestResponse,
  LlmConfigMetadata
} from './types';
import {
  getRegistryStrictUrl,
  getRegistryLenientUrl,
  getRegistryMalwareUrl
} from '../config';

interface Filters {
  status: AccessRequestStatus | 'ALL';
}

const REGISTRY_STRICT_URL = getRegistryStrictUrl(
  import.meta.env.VITE_REGISTRY_STRICT_URL || 'http://localhost:4873'
);
const REGISTRY_LENIENT_URL = getRegistryLenientUrl(
  import.meta.env.VITE_REGISTRY_LENIENT_URL || 'http://localhost:4874'
);
const REGISTRY_MALWARE_URL = getRegistryMalwareUrl(
  import.meta.env.VITE_REGISTRY_MALWARE_URL || 'http://localhost:4875'
);

function StatusBadge({ status }: { status: AccessRequestStatus }) {
  const base = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium';
  if (status === 'PENDING') {
    return (
      <span className={`${base} bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/40`}>
        Pending
      </span>
    );
  }
  if (status === 'APPROVED') {
    return (
      <span className={`${base} bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/40`}>
        Approved
      </span>
    );
  }
  return (
    <span className={`${base} bg-rose-500/10 text-rose-300 ring-1 ring-rose-500/40`}>
      Rejected
    </span>
  );
}

interface TokenModalProps {
  result: ApproveAccessRequestResponse | null;
  onClose: () => void;
}

function buildNpmrc(registryUrl: string, token: string): string {
  const trimmed = registryUrl.replace(/\/$/, '');
  const host = trimmed.replace(/^https?:\/\//, '');
  const base = `${trimmed}/`;

  let passwordB64: string;
  try {
    // btoa expects ASCII; tokens are URL-safe ASCII
    passwordB64 = typeof btoa === 'function' ? btoa(token) : token;
  } catch {
    // Fallback: return the raw token if encoding fails for any reason
    passwordB64 = token;
  }

  return [
    'ignore-scripts=true',
    `registry=${base}`,
    `@*:registry=${base}`,
    `//${host}/:username=token`,
    `//${host}/:_password=${passwordB64}`
  ].join('\n');
}

function buildMalwareNpmrc(registryUrl: string, token: string): string {
  const trimmed = registryUrl.replace(/\/$/, '');
  const host = trimmed.replace(/^https?:\/\//, '');
  const base = `${trimmed}/`;

  let passwordB64: string;
  try {
    passwordB64 = typeof btoa === 'function' ? btoa(token) : token;
  } catch {
    passwordB64 = token;
  }

  return [
    'ignore-scripts=true',
    `@malware:registry=${base}`,
    `//${host}/:username=token`,
    `//${host}/:_password=${passwordB64}`
  ].join('\n');
}

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

function downloadNpmrc(content: string, filename = '.npmrc'): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function TokenModal({ result, onClose }: TokenModalProps) {
  const [copiedStrict, setCopiedStrict] = useState(false);
  const [copiedLenient, setCopiedLenient] = useState(false);
  const [copiedMalware, setCopiedMalware] = useState(false);

  if (!result) return null;

  const strictNpmrc = buildNpmrc(REGISTRY_STRICT_URL, result.token);
  const lenientNpmrc = buildNpmrc(REGISTRY_LENIENT_URL, result.token);
  const malwareNpmrc = buildMalwareNpmrc(REGISTRY_MALWARE_URL, result.token);

  const handleCopyStrict = async () => {
    try {
      await copyToClipboard(strictNpmrc);
      setCopiedStrict(true);
      setTimeout(() => setCopiedStrict(false), 2000);
    } catch {
      // eslint-disable-next-line no-alert
      alert('Failed to copy .npmrc to clipboard');
    }
  };

  const handleCopyLenient = async () => {
    try {
      await copyToClipboard(lenientNpmrc);
      setCopiedLenient(true);
      setTimeout(() => setCopiedLenient(false), 2000);
    } catch {
      // eslint-disable-next-line no-alert
      alert('Failed to copy .npmrc to clipboard');
    }
  };

  const handleCopyMalware = async () => {
    try {
      await copyToClipboard(malwareNpmrc);
      setCopiedMalware(true);
      setTimeout(() => setCopiedMalware(false), 2000);
    } catch {
      // eslint-disable-next-line no-alert
      alert('Failed to copy .npmrc to clipboard');
    }
  };

  const handleDownloadStrict = () => {
    downloadNpmrc(strictNpmrc, '.npmrc-strict');
  };

  const handleDownloadLenient = () => {
    downloadNpmrc(lenientNpmrc, '.npmrc-lenient');
  };

  const handleDownloadMalware = () => {
    downloadNpmrc(malwareNpmrc, '.npmrc-malware');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900/95 p-4 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-50">
              Token for {result.email}
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Copy this token and send it securely to the requester. It will not be
              shown again.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 aspect-square items-center justify-center rounded-full text-xs leading-none text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-slate-300 dark:hover:bg-slate-800/80"
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        <div className="mt-3 space-y-3 text-xs">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Raw token
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-[11px] text-emerald-300">
              {result.token}
            </div>
          </div>

          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              .npmrc configuration (strict)
            </div>
            <pre className="whitespace-pre rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-[11px] text-slate-200">
              {strictNpmrc}
            </pre>
            <div className="mt-1 flex gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => void handleCopyStrict()}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10"
              >
                {copiedStrict ? 'Copied!' : 'Copy .npmrc'}
              </button>
              <button
                type="button"
                onClick={handleDownloadStrict}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10"
              >
                Download .npmrc
              </button>
            </div>
          </div>

          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              .npmrc configuration (lenient)
            </div>
            <pre className="whitespace-pre rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-[11px] text-slate-200">
              {lenientNpmrc}
            </pre>
            <div className="mt-1 flex gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => void handleCopyLenient()}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10"
              >
                {copiedLenient ? 'Copied!' : 'Copy .npmrc'}
              </button>
              <button
                type="button"
                onClick={handleDownloadLenient}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10"
              >
                Download .npmrc
              </button>
            </div>
          </div>

          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              .npmrc configuration (malware registry for @malware/*
              )
            </div>
            <pre className="whitespace-pre rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-[11px] text-slate-200">
              {malwareNpmrc}
            </pre>
            <div className="mt-1 flex gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => void handleCopyMalware()}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10"
              >
                {copiedMalware ? 'Copied!' : 'Copy .npmrc'}
              </button>
              <button
                type="button"
                onClick={handleDownloadMalware}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10"
              >
                Download .npmrc
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface EditTokenLimitModalProps {
  item: AccessRequestItem;
  adminToken: string;
  onClose: () => void;
  onSaved: () => void;
}

interface EditTokenLlmConfigModalProps {
  item: AccessRequestItem;
  adminToken: string;
  onClose: () => void;
}

interface EditOrgLlmConfigModalProps {
  item: AccessRequestItem;
  adminToken: string;
  onClose: () => void;
}

function EditTokenLimitModal({ item, adminToken, onClose, onSaved }: EditTokenLimitModalProps) {
  const current = item.tokenUsage;
  const [useDefault, setUseDefault] = useState(!current || current.isUsingDefaultLimit);
  const [maxRequests24h, setMaxRequests24h] = useState<string>(
    current && !current.isUsingDefaultLimit ? String(current.maxRequests) : ''
  );
  const [maxRequests7d, setMaxRequests7d] = useState<string>(
    current && !current.isUsingDefaultLimit ? String(current.maxRequests7d) : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item.tokenId) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let body: { maxRequests24h?: number | null; maxRequests7d?: number | null } = {};
      if (useDefault) {
        body = { maxRequests24h: null, maxRequests7d: null };
      } else {
        const parsed24h =
          maxRequests24h.trim().length > 0 ? Number(maxRequests24h) : NaN;
        const parsed7d = maxRequests7d.trim().length > 0 ? Number(maxRequests7d) : NaN;
        if (!Number.isFinite(parsed24h) || !Number.isFinite(parsed7d)) {
          setError('Both fields are required when not using default limits.');
          setSaving(false);
          return;
        }
        body = {
          maxRequests24h: parsed24h,
          maxRequests7d: parsed7d
        };
      }

      await updateTokenAuditRateLimit(item.tokenId!, body, adminToken);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update token limits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900/95 p-4 text-xs shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-50">Edit audit rate limit</h2>
            <p className="mt-1 text-[11px] text-slate-400">
              Control how many <span className="font-semibold">new package audits</span> this
              token can trigger in a given time window.
            </p>
            {current && (
              <p className="mt-1 text-[11px] text-slate-400">
                Current usage: <span className="font-mono text-slate-200">{current.currentWindowCount}</span>
                {' '}of <span className="font-mono text-slate-200">{current.maxRequests}</span> in
                {' '}<span className="font-mono text-slate-200">{current.windowSeconds}s</span> window · last 24h:
                {' '}<span className="font-mono text-slate-200">{current.last24hCount}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 aspect-square items-center justify-center rounded-full text-[11px] leading-none text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-slate-300 dark:hover:bg-slate-800/80"
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-3 space-y-3 text-[11px]">
          <label className="flex items-center gap-2 text-slate-200">
            <input
              type="checkbox"
              checked={useDefault}
              onChange={(e) => setUseDefault(e.target.checked)}
              className="h-3 w-3 rounded border-slate-600 bg-slate-900 text-emerald-500"
            />
            <span>Use default limits from configuration</span>
          </label>

          {!useDefault && (
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Max new audits per 24h
                </label>
                <input
                  type="number"
                  min={1}
                  value={maxRequests24h}
                  onChange={(e) => setMaxRequests24h(e.target.value)}
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Max new audits per 7 days
                </label>
                <input
                  type="number"
                  min={1}
                  value={maxRequests7d}
                  onChange={(e) => setMaxRequests7d(e.target.value)}
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>
          )}

          {error && <div className="text-[11px] text-rose-300">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:border-slate-500 hover:bg-slate-800/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded border border-emerald-500/70 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditTokenLlmConfigModal({ item, adminToken, onClose }: EditTokenLlmConfigModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [llmConfig, setLlmConfig] = useState<LlmConfigMetadata | null>(null);

  const [apiBaseUrl, setApiBaseUrl] = useState('https://api.openai.com/v1');
  const [model, setModel] = useState('gpt-5.1');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!item.tokenId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const existing = await getTokenLlmConfig(adminToken, item.tokenId);
        if (cancelled) return;
        if (existing) {
          setLlmConfig(existing);
          setApiBaseUrl(existing.apiBaseUrl);
          setModel(existing.model);
          setEnabled(existing.enabled);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message ?? 'Failed to load LLM config');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [adminToken, item.tokenId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item.tokenId) {
      onClose();
      return;
    }

    const finalApiBaseUrl = apiBaseUrl.trim() || 'https://api.openai.com/v1';
    const finalModel = model.trim() || 'gpt-5.1';

    if (!apiKey.trim()) {
      setError('apiKey is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await upsertTokenLlmConfig(adminToken, item.tokenId, {
        provider: 'openai',
        apiBaseUrl: finalApiBaseUrl,
        apiKey: apiKey.trim(),
        model: finalModel,
        enabled
      });
      setLlmConfig(saved);
      setApiKey('');
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save LLM config');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item.tokenId) {
      onClose();
      return;
    }

    setError(null);
    try {
      await deleteTokenLlmConfig(adminToken, item.tokenId);
      setLlmConfig(null);
      setApiBaseUrl('https://api.openai.com/v1');
      setModel('gpt-5.1');
      setApiKey('');
      setEnabled(true);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete LLM config');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900/95 p-4 text-xs shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-50">Token BYOK (LLM config)</h2>
              <p className="mt-1 text-[11px] text-slate-400">
                Attach a dedicated OpenAI-compatible API key and model to this token.
              </p>
              {llmConfig && (
                <p className="mt-1 text-[11px] text-slate-400">
                  Current: <span className="font-mono text-slate-200">{llmConfig.provider}</span>{' '}
                  · <span className="font-mono text-slate-200">{llmConfig.model}</span>{' '}
                  · <span className="font-mono text-slate-200">{llmConfig.apiBaseUrl}</span>{' '}
                  · key ****{llmConfig.apiKeyLast4 ?? '????'} (enabled:{' '}
                  {llmConfig.enabled ? 'yes' : 'no'})
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-7 aspect-square items-center justify-center rounded-full text-[11px] leading-none text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-slate-300 dark:hover:bg-slate-800/80"
              aria-label="Close dialog"
            >
              ✕
            </button>
          </div>

          {loading ? (
            <div className="mt-3 text-[11px] text-slate-400">Loading BYOK config...</div>
          ) : (
            <form onSubmit={handleSave} className="mt-3 space-y-3 text-[11px]">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Provider
                </label>
                <select
                  value="openai"
                  disabled
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50"
                >
                  <option value="openai">OpenAI-compatible (BYOK)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  API base URL
                </label>
                <input
                  type="text"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  placeholder="https://api.openai.com/v1"
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Model
                </label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="gpt-5.1"
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  API key
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Required for create/update"
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <label className="flex items-center gap-2 text-slate-200">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="h-3 w-3 rounded border-slate-600 bg-slate-900 text-emerald-500"
                />
                <span>Enabled for this token</span>
              </label>

              {error && <div className="text-[11px] text-rose-300">{error}</div>}

              <div className="flex items-center justify-between gap-2 pt-1">
                <div>
                  {llmConfig && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="rounded border border-rose-500/70 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-100 hover:bg-rose-500/20"
                    >
                      Delete config
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:border-slate-500 hover:bg-slate-800/60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded border border-emerald-500/70 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Save BYOK config'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete token BYOK config?"
          description="This will remove the stored LLM API key and model configuration for this token. This cannot be undone from the UI."
          variant="danger"
          confirmLabel="Delete config"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            await handleDelete();
            setShowDeleteConfirm(false);
          }}
        />
      )}
    </>
  );
}

function LoginView({ onLoggedIn }: LoginViewProps) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('admin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await login(username.trim(), password);
      onLoggedIn(res.token, res.username);
    } catch (err: any) {
      setError(err?.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white/90 p-5 shadow-xl dark:border-slate-700/80 dark:bg-slate-900/90 dark:shadow-2xl dark:shadow-black/60">
        <div className="mb-4 text-center">
          <div className="text-sm font-semibold tracking-wide text-emerald-400">
            NoPackageMalware
          </div>
          <p className="mt-1 text-xs text-slate-400">
            Because npm install shouldn’t feel like Russian roulette.
          </p>
          <h1 className="mt-1 text-lg font-semibold text-slate-50">
            Access admin login
          </h1>
          <p className="mt-1 text-xs text-slate-400">
            Default credentials (dev): <code>admin / admin</code>
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          {error && (
            <div className="text-xs text-rose-300">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="mt-1 inline-flex w-full items-center justify-center rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-medium text-emerald-950 shadow-sm hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditOrgLlmConfigModal({ item, adminToken, onClose }: EditOrgLlmConfigModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [llmConfig, setLlmConfig] = useState<LlmConfigMetadata | null>(null);

  const [apiBaseUrl, setApiBaseUrl] = useState('https://api.openai.com/v1');
  const [model, setModel] = useState('gpt-5.1');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!item.orgId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const existing = await getOrgLlmConfig(adminToken, item.orgId);
        if (cancelled) return;
        if (existing) {
          setLlmConfig(existing);
          setApiBaseUrl(existing.apiBaseUrl);
          setModel(existing.model);
          setEnabled(existing.enabled);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message ?? 'Failed to load org LLM config');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [adminToken, item.orgId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item.orgId) {
      onClose();
      return;
    }

    const finalApiBaseUrl = apiBaseUrl.trim() || 'https://api.openai.com/v1';
    const finalModel = model.trim() || 'gpt-5.1';

    if (!apiKey.trim()) {
      setError('apiKey is required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await upsertOrgLlmConfig(adminToken, item.orgId, {
        provider: 'openai',
        apiBaseUrl: finalApiBaseUrl,
        apiKey: apiKey.trim(),
        model: finalModel,
        enabled
      });
      setLlmConfig(saved);
      setApiKey('');
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save org LLM config');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item.orgId) {
      onClose();
      return;
    }

    setError(null);
    try {
      await deleteOrgLlmConfig(adminToken, item.orgId);
      setLlmConfig(null);
      setApiBaseUrl('https://api.openai.com/v1');
      setModel('gpt-5.1');
      setApiKey('');
      setEnabled(true);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete org LLM config');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900/95 p-4 text-xs shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-50">Org BYOK (LLM config)</h2>
              <p className="mt-1 text-[11px] text-slate-400">
                Attach a dedicated OpenAI-compatible API key and model to this org. Tokens may
                override this with their own BYOK config.
              </p>
              {llmConfig && (
                <p className="mt-1 text-[11px] text-slate-400">
                  Current: <span className="font-mono text-slate-200">{llmConfig.provider}</span>{' '}
                  · <span className="font-mono text-slate-200">{llmConfig.model}</span>{' '}
                  · <span className="font-mono text-slate-200">{llmConfig.apiBaseUrl}</span>{' '}
                  · key ****{llmConfig.apiKeyLast4 ?? '????'} (enabled:{' '}
                  {llmConfig.enabled ? 'yes' : 'no'})
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-7 aspect-square items-center justify-center rounded-full text-[11px] leading-none text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-slate-300 dark:hover:bg-slate-800/80"
              aria-label="Close dialog"
            >
              ✕
            </button>
          </div>

          {loading ? (
            <div className="mt-3 text-[11px] text-slate-400">Loading org BYOK config...</div>
          ) : (
            <form onSubmit={handleSave} className="mt-3 space-y-3 text-[11px]">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Provider
                </label>
                <select
                  value="openai"
                  disabled
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50"
                >
                  <option value="openai">OpenAI-compatible (BYOK)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  API base URL
                </label>
                <input
                  type="text"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  placeholder="https://api.openai.com/v1"
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Model
                </label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="gpt-5.1"
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  API key
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Required for create/update"
                  className="block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-50 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              <label className="flex items-center gap-2 text-slate-200">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="h-3 w-3 rounded border-slate-600 bg-slate-900 text-emerald-500"
                />
                <span>Enabled for this org</span>
              </label>

              {error && <div className="text-[11px] text-rose-300">{error}</div>}

              <div className="flex items-center justify-between gap-2 pt-1">
                <div>
                  {llmConfig && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="rounded border border-rose-500/70 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-100 hover:bg-rose-500/20"
                    >
                      Delete config
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:border-slate-500 hover:bg-slate-800/60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded border border-emerald-500/70 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : 'Save org BYOK config'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete org BYOK config?"
          description="This will remove the stored LLM API key and model configuration for this org. This cannot be undone from the UI."
          variant="danger"
          confirmLabel="Delete config"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            await handleDelete();
            setShowDeleteConfirm(false);
          }}
        />
      )}
    </>
  );
}

type ConfirmActionType = 'approve' | 'reject' | 'renew';

interface PendingConfirm {
  type: ConfirmActionType;
  item: AccessRequestItem;
}

interface ConfirmDialogProps {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

function ConfirmDialog({
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onCancel]);

  const handleConfirmClick = async () => {
    const maybePromise = onConfirm();
    if (maybePromise && typeof (maybePromise as any).then === 'function') {
      try {
        setLoading(true);
        await maybePromise;
      } finally {
        setLoading(false);
      }
    }
  };

  const confirmClasses =
    variant === 'danger'
      ? 'inline-flex items-center justify-center rounded-md border border-rose-500/70 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-100 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-60'
      : 'inline-flex items-center justify-center rounded-md border border-emerald-500/70 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-xl border border-slate-700 bg-slate-900/95 p-4 text-xs text-slate-100 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-50">{title}</h2>
            {description && (
              <p className="mt-1 text-[11px] text-slate-400">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-7 aspect-square items-center justify-center rounded-full text-xs leading-none text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-slate-300 dark:hover:bg-slate-800/80"
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>
        <div className="mt-4 flex justify-end gap-2 text-[11px]">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-slate-500 hover:bg-slate-800/60"
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirmClick()}
            className={confirmClasses}
            disabled={loading}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [filters, setFilters] = useState<Filters>({ status: 'PENDING' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [items, setItems] = useState<AccessRequestItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approveResult, setApproveResult] = useState<ApproveAccessRequestResponse | null>(
    null
  );
  const [editingTokenItem, setEditingTokenItem] = useState<AccessRequestItem | null>(null);
  const [editingTokenLlmItem, setEditingTokenLlmItem] = useState<AccessRequestItem | null>(null);
  const [editingOrgLlmItem, setEditingOrgLlmItem] = useState<AccessRequestItem | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

  const [token, setToken] = useState<string | undefined>(undefined);
  const [adminUser, setAdminUser] = useState<string | null>(null);

  // Load token from localStorage on first render
  useEffect(() => {
    const storedToken = localStorage.getItem('access-admin-token');
    const storedUser = localStorage.getItem('access-admin-username');
    if (storedToken) {
      setToken(storedToken);
      setAdminUser(storedUser || null);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      // Not authenticated yet; do not attempt to load
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchAccessRequests(
          {
            page,
            pageSize,
            status: filters.status
          },
          token
        );
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message ?? 'Unknown error');
        setItems([]);
        setTotal(0);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, filters.status, token]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextStatus = e.target.value as AccessRequestStatus | 'ALL';
    setFilters({ status: nextStatus });
    setPage(1);
  };

  const handlePrev = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const handleNext = () => {
    setPage((p) => Math.min(totalPages, p + 1));
  };

  const handleApprove = async (item: AccessRequestItem) => {
    const currentToken = token;
    if (!currentToken) return;
    try {
      const res = await approveAccessRequest(item.id, currentToken);
      setApproveResult(res);
      const refreshed = await fetchAccessRequests(
        {
          page,
          pageSize,
          status: filters.status
        },
        currentToken
      );
      setItems(refreshed.items);
      setTotal(refreshed.total);
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.message ?? 'Failed to approve request');
    }
  };

  const handleReject = async (item: AccessRequestItem) => {
    const currentToken = token;
    if (!currentToken) return;
    try {
      await rejectAccessRequest(item.id, currentToken);
      const refreshed = await fetchAccessRequests(
        {
          page,
          pageSize,
          status: filters.status
        },
        currentToken
      );
      setItems(refreshed.items);
      setTotal(refreshed.total);
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.message ?? 'Failed to reject request');
    }
  };

  const handleRenew = async (item: AccessRequestItem) => {
    const currentToken = token;
    if (!currentToken) return;
    try {
      const res = await renewAccessRequest(item.id, currentToken);
      setApproveResult(res);
      const refreshed = await fetchAccessRequests(
        {
          page,
          pageSize,
          status: filters.status
        },
        currentToken
      );
      setItems(refreshed.items);
      setTotal(refreshed.total);
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.message ?? 'Failed to renew token');
    }
  };

  const handleTokenLimitsSaved = async () => {
    const currentToken = token;
    if (!currentToken) return;
    try {
      const refreshed = await fetchAccessRequests(
        {
          page,
          pageSize,
          status: filters.status
        },
        currentToken
      );
      setItems(refreshed.items);
      setTotal(refreshed.total);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh access requests after saving token limits', err);
    }
  };

  const handleLoggedIn = (newToken: string, username: string) => {
    setToken(newToken);
    setAdminUser(username);
    localStorage.setItem('access-admin-token', newToken);
    localStorage.setItem('access-admin-username', username);
  };

  const handleLogout = () => {
    setToken(undefined);
    setAdminUser(null);
    localStorage.removeItem('access-admin-token');
    localStorage.removeItem('access-admin-username');
    setItems([]);
    setTotal(0);
  };

  if (!token) {
    return <LoginView onLoggedIn={handleLoggedIn} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
      <div className="border-b border-slate-200/80 bg-white/80 backdrop-blur-sm dark:border-slate-800/80 dark:bg-slate-950/90">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div>
            <div className="text-sm font-semibold tracking-wide text-emerald-400">
              NoPackageMalware
            </div>
            <p className="mt-0.5 text-xs text-slate-400">
              Because npm install shouldn’t feel like Russian roulette.
            </p>
            <h1 className="text-lg font-semibold text-slate-50">
              Access admin dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <div className="hidden md:block">
              Admin API:{' '}
              <code className="rounded bg-slate-900 px-2 py-0.5">
                {import.meta.env.VITE_ACCESS_ADMIN_API_URL ?? 'http://localhost:4100'}
              </code>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded bg-slate-900 px-2 py-0.5 text-[11px] text-slate-200">
                {adminUser ?? 'admin'}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-200 hover:border-rose-500 hover:bg-rose-500/10 hover:text-rose-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-4 text-sm">
        <section className="flex flex-col gap-3 rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Filters
            </div>
            <div className="mt-2 flex items-center gap-3">
              <label className="text-xs text-slate-300">
                Status{' '}
                <select
                  value={filters.status}
                  onChange={handleStatusChange}
                  className="ml-1 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </label>
            </div>
          </div>
          <div className="text-xs text-slate-400">
            Registry URLs used for .npmrc instructions:
            <div className="mt-1 space-y-0.5">
              <div>
                <span className="font-semibold text-slate-300">Strict:</span>{' '}
                <code className="rounded bg-slate-950 px-1.5 py-0.5 text-[11px]">
                  {REGISTRY_STRICT_URL}
                </code>
              </div>
              <div>
                <span className="font-semibold text-slate-300">Lenient:</span>{' '}
                <code className="rounded bg-slate-950 px-1.5 py-0.5 text-[11px]">
                  {REGISTRY_LENIENT_URL}
                </code>
              </div>
              <div>
                <span className="font-semibold text-slate-300">Malware (@malware/*):</span>{' '}
                <code className="rounded bg-slate-950 px-1.5 py-0.5 text-[11px]">
                  {REGISTRY_MALWARE_URL}
                </code>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-700/70 bg-slate-900/80">
          <div className="border-b border-slate-700/60 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-400">
            Access requests
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800 text-xs">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-400">
                    Email
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-400">
                    Org
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-400">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-400">
                    Created
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-400">
                    Approved / Rejected
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-400">
                    Token / usage
                  </th>
                  <th className="px-4 py-2 text-right font-semibold text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 text-center text-slate-400">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 text-center text-rose-300">
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-4 text-center text-slate-400">
                      No access requests found.
                    </td>
                  </tr>
                )}
                {!loading && !error &&
                  items.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-800/40">
                      <td className="px-4 py-2 align-top text-slate-100">
                        <div className="font-medium">{item.email}</div>
                      </td>
                      <td className="px-4 py-2 align-top text-slate-200">
                        <div className="flex items-center gap-2">
                          <span>{item.orgName}</span>
                          {item.orgId && token && (
                            <button
                              type="button"
                              onClick={() => setEditingOrgLlmItem(item)}
                              className="rounded border border-slate-600 bg-slate-900/60 px-1.5 py-0.5 text-[10px] text-slate-200 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-100"
                            >
                              Org BYOK
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 align-top">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-2 align-top text-slate-300">
                        {item.createdAt}
                      </td>
                      <td className="px-4 py-2 align-top text-slate-300">
                        {item.approvedAt || item.rejectedAt || '-'}
                      </td>
                      <td className="px-4 py-2 align-top text-slate-300">
                        {item.tokenLast4 ? (
                          <div className="space-y-0.5">
                            <div>Token: ****{item.tokenLast4}</div>
                            {item.tokenUsage && (
                              <div className="text-[11px] text-slate-400 space-y-0.5">
                                <div>
                                  24h window: {item.tokenUsage.currentWindowCount} / {item.tokenUsage.maxRequests}
                                  {' '}({item.tokenUsage.windowSeconds}s bucket)
                                </div>
                                <div>
                                  7 days: {item.tokenUsage.last7dCount} / {item.tokenUsage.maxRequests7d}
                                  {' '}· last 24h (calendar): {item.tokenUsage.last24hCount}{' '}
                                  <span className="italic">
                                    {item.tokenUsage.isUsingDefaultLimit ? ' (default)' : ' (custom)'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-2 align-top text-right">
                        {item.status === 'PENDING' ? (
                          <div className="inline-flex gap-2">
                            <button
                              type="button"
                              onClick={() => setPendingConfirm({ type: 'approve', item })}
                              className="rounded-md border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingConfirm({ type: 'reject', item })}
                              className="rounded-md border border-rose-500/60 bg-rose-500/10 px-2 py-1 text-[11px] font-medium text-rose-200 hover:bg-rose-500/20"
                            >
                              Reject
                            </button>
                          </div>
                        ) : item.tokenId ? (
                          <div className="inline-flex gap-2">
                            <button
                              type="button"
                              onClick={() => setEditingTokenItem(item)}
                              className="rounded-md border border-slate-600 bg-slate-800/60 px-2 py-1 text-[11px] font-medium text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-100"
                            >
                              Edit limit
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingTokenLlmItem(item)}
                              className="rounded-md border border-slate-600 bg-slate-800/60 px-2 py-1 text-[11px] font-medium text-slate-100 hover:border-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-100"
                            >
                              BYOK
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingConfirm({ type: 'renew', item })}
                              className="rounded-md border border-emerald-500/60 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
                            >
                              Renew token
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-500">
                            No actions
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-800 px-4 py-2 text-[11px] text-slate-300">
            <div>
              {total === 0
                ? '0 of 0'
                : `${(page - 1) * pageSize + 1}-${Math.min(total, page * pageSize)} of ${total}`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrev}
                disabled={page <= 1}
                className="inline-flex items-center rounded border border-slate-700/70 px-2 py-1 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-40 hover:border-slate-500 hover:bg-slate-800/60"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={handleNext}
                disabled={page >= totalPages}
                className="inline-flex items-center rounded border border-slate-700/70 px-2 py-1 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-40 hover:border-slate-500 hover:bg-slate-800/60"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </main>

      <TokenModal result={approveResult} onClose={() => setApproveResult(null)} />
      {editingTokenItem && token && (
        <EditTokenLimitModal
          item={editingTokenItem}
          adminToken={token}
          onClose={() => setEditingTokenItem(null)}
          onSaved={handleTokenLimitsSaved}
        />
      )}
      {editingTokenLlmItem && token && (
        <EditTokenLlmConfigModal
          item={editingTokenLlmItem}
          adminToken={token}
          onClose={() => setEditingTokenLlmItem(null)}
        />
      )}
      {editingOrgLlmItem && token && (
        <EditOrgLlmConfigModal
          item={editingOrgLlmItem}
          adminToken={token}
          onClose={() => setEditingOrgLlmItem(null)}
        />
      )}
      {pendingConfirm && (
        <ConfirmDialog
          title={
            pendingConfirm.type === 'approve'
              ? 'Approve access request?'
              : pendingConfirm.type === 'reject'
                ? 'Reject access request?'
                : 'Renew access token?'
          }
          description={
            pendingConfirm.type === 'approve'
              ? `This will approve access for ${pendingConfirm.item.email} and generate a new access token.`
              : pendingConfirm.type === 'reject'
                ? `This will reject the access request for ${pendingConfirm.item.email}.`
                : `This will renew the access token for ${pendingConfirm.item.email}.`
          }
          variant={pendingConfirm.type === 'reject' ? 'danger' : 'default'}
          confirmLabel={
            pendingConfirm.type === 'approve'
              ? 'Approve request'
              : pendingConfirm.type === 'reject'
                ? 'Reject request'
                : 'Renew token'
          }
          onCancel={() => setPendingConfirm(null)}
          onConfirm={async () => {
            if (!pendingConfirm) return;
            if (pendingConfirm.type === 'approve') {
              await handleApprove(pendingConfirm.item);
            } else if (pendingConfirm.type === 'reject') {
              await handleReject(pendingConfirm.item);
            } else {
              await handleRenew(pendingConfirm.item);
            }
            setPendingConfirm(null);
          }}
        />
      )}
    </div>
  );
}

export default App;
